import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { cx } from '../cx';
import { ArrowRight } from './icons';
import ui from '../ui.module.css';
import s from './DesktopDownload.module.css';

/* =========================================================================
   Download for desktop.

   Two endpoints, neither of which needs auth:

     GET /v1/download            sniffs the User-Agent and sends the right
                                 installer. The primary button is a plain
                                 anchor at this URL — the happy path needs no
                                 JavaScript at all, and the server, not the
                                 browser, makes the final call.
     GET /v1/releases/desktop    the manifest, used only to label things: the
                                 version, the file sizes, and the explicit
                                 per-platform links in the disclosure.

   The manifest is legitimately empty right now — nothing has shipped. That is
   not an error state and it must not look like one, so when `version` is null
   (or the fetch fails, or it is still in flight) this renders the studio call
   to action and one quiet line instead. There is never a broken button.
   ========================================================================= */

const API_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:8787').replace(/\/+$/, '');
const DOWNLOAD_URL = `${API_URL}/v1/download`;
const MANIFEST_URL = `${API_URL}/v1/releases/desktop`;

type DesktopOs = 'mac' | 'win' | 'linux';

interface Artifact {
  file: string;
  os: DesktopOs;
  arch: 'arm64' | 'x64' | 'universal';
  kind: string;
  bytes: number;
  sha256: string;
}

interface Manifest {
  version: string;
  releasedAt: string | null;
  artifacts: Artifact[];
}

const OS_LABEL: Record<DesktopOs, string> = {
  mac: 'macOS',
  win: 'Windows',
  linux: 'Linux',
};

const ARCH_LABEL: Record<Artifact['arch'], string> = {
  arm64: 'Apple silicon / ARM64',
  x64: 'Intel / x64',
  universal: 'Universal',
};

/**
 * Which platform to *name* on the button. Only ever used for the label — the
 * href always points at `/v1/download` so a wrong guess here cannot send
 * anybody the wrong installer.
 */
function detectOs(): DesktopOs | null {
  if (typeof navigator === 'undefined') return null;
  const uaData = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData;
  const platform = (uaData?.platform ?? navigator.platform ?? '').toLowerCase();
  const ua = (navigator.userAgent ?? '').toLowerCase();
  const hay = `${platform} ${ua}`;
  // Order matters: iOS and Android must not be labelled as desktop platforms.
  if (/iphone|ipad|ipod|android/.test(hay)) return null;
  if (/mac|darwin/.test(hay)) return 'mac';
  if (/win/.test(hay)) return 'win';
  if (/linux|x11|ubuntu|fedora/.test(hay)) return 'linux';
  return null;
}

function isArtifact(value: unknown): value is Artifact {
  if (typeof value !== 'object' || value === null) return false;
  const a = value as Record<string, unknown>;
  return (
    typeof a.file === 'string' &&
    (a.os === 'mac' || a.os === 'win' || a.os === 'linux') &&
    (a.arch === 'arm64' || a.arch === 'x64' || a.arch === 'universal') &&
    typeof a.bytes === 'number'
  );
}

/** Network data, so it is checked rather than trusted. */
function parseManifest(value: unknown): Manifest | null {
  if (typeof value !== 'object' || value === null) return null;
  const raw = value as Record<string, unknown>;
  if (typeof raw.version !== 'string' || raw.version.length === 0) return null;
  const artifacts = Array.isArray(raw.artifacts) ? raw.artifacts.filter(isArtifact) : [];
  if (artifacts.length === 0) return null;
  return {
    version: raw.version,
    releasedAt: typeof raw.releasedAt === 'string' ? raw.releasedAt : null,
    artifacts,
  };
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

function formatDate(value: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Fetch the manifest once. Any failure is treated exactly like "nothing yet". */
function useDesktopRelease(): Manifest | null {
  const [manifest, setManifest] = useState<Manifest | null>(null);

  useEffect(() => {
    let live = true;
    const controller = typeof AbortController === 'undefined' ? null : new AbortController();

    fetch(MANIFEST_URL, { signal: controller?.signal })
      .then((response) => (response.ok ? response.json() : null))
      .then((json) => {
        if (live) setManifest(parseManifest(json));
      })
      .catch(() => {
        /* No manifest is the expected state today. Nothing to report. */
      });

    return () => {
      live = false;
      controller?.abort();
    };
  }, []);

  return manifest;
}

export interface DesktopDownloadProps {
  className?: string;
  /** `page` sets the block on its own rules; `footer` keeps it tight. */
  variant?: 'page' | 'footer';
}

export function DesktopDownload({ className, variant = 'page' }: DesktopDownloadProps) {
  const manifest = useDesktopRelease();
  const [os, setOs] = useState<DesktopOs | null>(null);

  // Platform detection is client-only, so it runs after mount rather than
  // changing what the first paint says.
  useEffect(() => {
    setOs(detectOs());
  }, []);

  const label = os ? `Download for ${OS_LABEL[os]}` : 'Download for desktop';

  if (!manifest) {
    return (
      <div className={cx(s.block, s[variant], className)}>
        <p className={s.kicker}>Desktop</p>
        <Link to="/auth" className={cx(ui.btn, ui.primary, s.cta)}>
          Open the studio
          <ArrowRight size={16} />
        </Link>
        <p className={s.note}>
          Desktop builds are on the way. Everything works in the browser today, and the studio is
          the same studio.
        </p>
      </div>
    );
  }

  return (
    <div className={cx(s.block, s[variant], className)}>
      <p className={s.kicker}>Desktop</p>

      <a className={cx(ui.btn, ui.primary, s.cta)} href={DOWNLOAD_URL}>
        {label}
        <ArrowRight size={16} />
      </a>

      <p className={s.note}>
        Version {manifest.version}
        {formatDate(manifest.releasedAt) ? ` · ${formatDate(manifest.releasedAt)}` : ''}
      </p>

      <details className={s.other}>
        <summary className={s.summary}>Other platforms</summary>
        <ul className={s.list}>
          {manifest.artifacts.map((artifact) => (
            <li key={`${artifact.os}-${artifact.arch}-${artifact.file}`} className={s.row}>
              <a
                className={s.rowLink}
                href={`${DOWNLOAD_URL}?os=${artifact.os}&arch=${artifact.arch}`}
              >
                {OS_LABEL[artifact.os]}
                <span className={s.rowArch}>{ARCH_LABEL[artifact.arch]}</span>
              </a>
              <span className={s.rowMeta}>
                {artifact.kind ? `${artifact.kind} · ` : ''}
                {formatBytes(artifact.bytes)}
              </span>
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}
